import { endOfDay, endOfToday, isAfter, isBefore, startOfDay } from 'date-fns'
import { State } from 'index'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { PER_PAGE } from 'constants/storeCustomer'
import { parseHirakanaToKanakana } from 'lib/helper'
import { getStoreRef } from 'reducks/auth/selectors'
import { getStoreCreatedAt } from 'reducks/store/selectors'
import { fetchStoreCustomers } from 'repos/storeCustomer'
import { StoreCustomerType } from 'types/storeCustomer'

type FavoriteCondition = 'favorite' | 'not_favorite' | 'all'

const useSearchCustomers = () => {
  const dispatch = useDispatch()
  const storeCreatedAt = useSelector((state: State) => getStoreCreatedAt(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const pathname = useSelector((state: State) => state.router.location.pathname)
  const query = useSelector((state: State) => state.router.location.query)
  const currentPage = Number(query?.page ?? '1')

  const [storeCustomers, setStoreCustomers] = useState<StoreCustomerType[]>([])
  const [keyword, setKeyword] = useState<string>('')
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [favoriteCondition, setFavoriteCondition] = useState<FavoriteCondition>('favorite')
  const [minPurchaseCount, setMinPurchaseCount] = useState<number>(1)
  const [maxPurchaseCount, setMaxPurchaseCount] = useState<number>(-1)
  const [startOfPeriod, setStartOfPeriod] = useState<string>('')
  const [endOfPeriod, setEndOfPeriod] = useState<string>('')

  const initPage = () => {
    dispatch(push(pathname))
  }

  const handleKeyword = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (value === '') {
        setSearchKeyword('')
        initPage()
      }
      setKeyword(value)
    },
    [setKeyword, setSearchKeyword, initPage]
  )

  const isFavoriteCondition = (id: string): id is FavoriteCondition => {
    return id === 'favorite' || id === 'not_favorite' || id === 'all'
  }

  const handleFavoriteCondition = (event: ChangeEvent<HTMLInputElement>) => {
    const id = event.target.id
    if (isFavoriteCondition(id)) {
      setFavoriteCondition((prevState) => {
        return prevState !== id ? id : 'not_favorite'
      })
    }
  }

  const searchCustomers = useCallback(() => {
    setSearchKeyword(keyword)
    initPage()
  }, [keyword, setSearchKeyword, initPage])

  const handleMinPurchaseCount = useCallback(
    (value: string) => {
      // maxPurchaseCount より大きな値は設定できない
      if (value !== '' && maxPurchaseCount !== -1 && Number(value) > maxPurchaseCount) {
        return
      }
      setMinPurchaseCount(Number(value))
      initPage()
    },
    [setMinPurchaseCount, maxPurchaseCount, initPage]
  )

  const handleMaxPurchaseCount = useCallback(
    (value: string) => {
      // minPurchaseCount より小さな値は設定できない
      if (value !== '-1' && Number(value) < minPurchaseCount) {
        return
      }
      setMaxPurchaseCount(Number(value))
      initPage()
    },
    [setMaxPurchaseCount, minPurchaseCount, initPage]
  )

  const handleStartOfPeriod = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value !== '' && endOfPeriod !== '' && e.target.value > endOfPeriod) {
        return
      }
      setStartOfPeriod(e.target.value)
      initPage()
    },
    [setStartOfPeriod, endOfPeriod, initPage]
  )

  const handleEndOfPeriod = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value !== '' && startOfPeriod !== '' && e.target.value < startOfPeriod) {
        return
      }
      setEndOfPeriod(e.target.value)
      initPage()
    },
    [setEndOfPeriod, startOfPeriod, initPage]
  )

  // 検索条件に応じて必要なデータを取得する
  useEffect(() => {
    ;(async () => {
      if (!storeRef) return
      const storeCustomers = await fetchStoreCustomers(storeRef)
      setStoreCustomers(storeCustomers)
    })()
  }, [storeRef])

  const filteredStoreCustomers = useMemo(() => {
    if (!storeCreatedAt) {
      return []
    }
    const regexp = new RegExp('^(?=.*' + searchKeyword + ').*$', 'g')
    const hirakanaRegexp = new RegExp('^(?=.*' + parseHirakanaToKanakana(searchKeyword) + ').*$', 'g')
    const upperRegexp = new RegExp('^(?=.*' + searchKeyword.toUpperCase() + ').*$', 'g')
    const lowerRegexp = new RegExp('^(?=.*' + searchKeyword.toLowerCase() + ').*$', 'g')
    const startedDate = startOfPeriod ? startOfDay(new Date(startOfPeriod)) : startOfDay(storeCreatedAt.toDate())
    const endedDate = endOfPeriod ? endOfDay(new Date(endOfPeriod)) : endOfToday()
    return storeCustomers
      .filter(
        (storeCustomer) =>
          // お気に入り登録のみで未購入のユーザーは絞り込まない
          (storeCustomer.last_purchased_at === null ||
            // 最終購入日の範囲内かどうか
            (isAfter(storeCustomer.last_purchased_at.toDate(), startedDate) &&
              isBefore(storeCustomer.last_purchased_at.toDate(), endedDate))) &&
          // 購入回数の範囲内かどうか
          storeCustomer.purchase_count >= minPurchaseCount &&
          (maxPurchaseCount === -1 || maxPurchaseCount >= storeCustomer.purchase_count) &&
          // お気に入り登録ONの場合はお気に入り登録済みのユーザーのみ表示する
          // お気に入り登録OFFの場合はお気に入り登録済みユーザー以外を表示
          // すべての場合は全ユーザー表示
          (favoriteCondition !== 'favorite' || storeCustomer.is_favorite) &&
          (favoriteCondition !== 'not_favorite' || !storeCustomer.is_favorite) &&
          // 検索キーワードが空の場合は全てのユーザーを表示して、それ以外は名前か電話番号に検索キーワードが含まれるユーザーを表示する
          (searchKeyword === '' ||
            regexp.test(storeCustomer.name) ||
            hirakanaRegexp.test(storeCustomer.name) ||
            upperRegexp.test(storeCustomer.name.toUpperCase()) ||
            lowerRegexp.test(storeCustomer.name.toLowerCase()) ||
            regexp.test(storeCustomer.tel))
      )
      .sort((a, b) => (b.last_purchased_at?.toDate().getTime() ?? 0) - (a.last_purchased_at?.toDate().getTime() ?? 0))
  }, [
    storeCustomers,
    favoriteCondition,
    searchKeyword,
    minPurchaseCount,
    maxPurchaseCount,
    storeCreatedAt,
    startOfPeriod,
    endOfPeriod,
  ])

  const pagedStoreCustomers = useMemo(
    () => filteredStoreCustomers.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE),
    [filteredStoreCustomers, currentPage, PER_PAGE]
  )

  const minPurchaseCountOptions = useMemo(() => {
    // storeCustomers が0件の場合は初期値の1を設定する
    if (storeCustomers.length === 0) {
      setMinPurchaseCount(1)
      return [
        { label: '0回以上', value: '0' },
        { label: '1回以上', value: '1' },
      ]
    }
    const storeCustomerMaxPurchaseCount = Math.max(
      ...storeCustomers.map((storeCustomer) => storeCustomer.purchase_count)
    )
    const length = maxPurchaseCount !== -1 ? maxPurchaseCount : storeCustomerMaxPurchaseCount
    // 購入回数 0 のお客様しかいない場合
    if (length === 0) {
      setMinPurchaseCount(0)
      return [{ label: '0回以上', value: '0' }]
    } else {
      return Array.from({ length: length + 1 }, (_, index) => index).map((index) => ({
        label: `${index}回以上`,
        value: index.toString(),
      }))
    }
  }, [storeCustomers, setMinPurchaseCount, maxPurchaseCount])

  const maxPurchaseCountOptions = useMemo(() => {
    if (storeCustomers.length === 0) {
      setMaxPurchaseCount(-1)
      return [{ label: '未設定', value: '-1' }]
    }
    const storeCustomerMaxPurchaseCount = Math.max(
      ...storeCustomers.map((storeCustomer) => storeCustomer.purchase_count)
    )
    const length = storeCustomerMaxPurchaseCount - minPurchaseCount
    return [
      { label: '未設定', value: '-1' },
      ...Array.from({ length: length + 1 }, (_, index) => index + minPurchaseCount).map((index) => ({
        label: `${index}回以下`,
        value: index.toString(),
      })),
    ]
  }, [storeCustomers, setMaxPurchaseCount, minPurchaseCount])

  return {
    storeCustomers: pagedStoreCustomers,
    keyword,
    favoriteCondition,
    maxPurchaseCount,
    minPurchaseCount,
    startOfPeriod,
    endOfPeriod,
    handleKeyword,
    handleFavoriteCondition,
    handleMaxPurchaseCount,
    handleMinPurchaseCount,
    handleStartOfPeriod,
    handleEndOfPeriod,
    maxPurchaseCountOptions,
    minPurchaseCountOptions,
    searchCustomers,
    totalCustomerAmount: filteredStoreCustomers.length,
  }
}

export default useSearchCustomers
